import api from './api';

/**
 * Service for user-related API calls
 */
class UserService {
  /**
   * Get user profile data
   * 
   * @returns {Promise} Promise with user profile
   */
  getProfile() {
    return api.get(`/v1/users/profile`);
  }
  
  /**
   * Update user profile data
   * 
   * @param {Object} profileData - New profile data
   * @returns {Promise} Promise with updated user profile
   */
  updateProfile(profileData) {
    return api.put(`/v1/users/profile`, profileData);
  }
  
  /**
   * Get user's devices
   * 
   * @returns {Promise} Promise with user devices
   */
  getDevices() {
    return api.get(`/v1/users/devices`);
  }
  
  /**
   * Update device trust status
   * 
   * @param {string} deviceId - Device ID
   * @param {boolean} trusted - Trust status
   * @returns {Promise} Promise with updated device
   */
  updateDeviceTrust(deviceId, trusted) {
    return api.put(`/v1/users/devices/${deviceId}/trust`, { trusted });
  }
  
  /**
   * Get user's active sessions
   * 
   * @returns {Promise} Promise with user sessions
   */
  getSessions() {
    return api.get(`/v1/users/sessions`);
  }
  
  /**
   * Terminate a specific session
   * 
   * @param {string} sessionId - Session ID to terminate
   * @returns {Promise} Promise with result
   */
  terminateSession(sessionId) {
    return api.delete(`/v1/users/sessions/${sessionId}`);
  }
  
  /**
   * Terminate all sessions except current
   * 
   * @returns {Promise} Promise with result
   */
  terminateAllSessions() {
    return api.delete(`/v1/users/sessions`);
  }
  
  /**
   * Change user password
   * 
   * @param {Object} passwordData - Password data
   * @param {string} passwordData.currentPassword - Current password
   * @param {string} passwordData.newPassword - New password
   * @param {string} passwordData.passwordConfirmation - Password confirmation
   * @returns {Promise} Promise with result
   */
  changePassword(passwordData) {
    return api.post(`/v1/users/password`, passwordData);
  }
  
  /**
   * Update notification preferences
   * 
   * @param {Object} preferences - Notification preferences
   * @returns {Promise} Promise with updated preferences
   */
  updateNotificationPreferences(preferences) {
    return api.put(`/v1/users/notifications`, preferences);
  }
  
  /**
   * Initiate account deletion process (sends confirmation email)
   * 
   * @param {Object} data - Deletion request data
   * @param {string} data.confirmationText - User must type "DELETE"
   * @param {string} data.reason - Optional reason for deletion
   * @returns {Promise} Promise with result
   */
  initiateAccountDeletion(data) {
    return api.post(`/v1/users/profile/delete/initiate`, data);
  }

  /**
   * Get user info by account deletion token (public endpoint)
   * 
   * @param {string} token - Deletion confirmation token
   * @returns {Promise} Promise with user profile data
   */
  getUserInfoByDeletionToken(token) {
    return api.get(`/v1/users/public/delete/user-info?token=${token}`);
  }

  /**
   * Confirm account deletion with email token and password
   * 
   * @param {Object} data - Confirmation data
   * @param {string} data.token - Deletion confirmation token
   * @param {string} data.password - User password for verification
   * @param {string} data.confirmationText - User must type "DELETE"
   * @returns {Promise} Promise with result
   */
  confirmAccountDeletion(data) {
    return api.post(`/v1/users/public/delete/confirm`, data);
  }

  /**
   * Request account deletion (legacy)
   * 
   * @param {Object} data - Deletion request data
   * @param {string} data.password - User password for confirmation
   * @param {string} data.reason - Optional reason for deletion
   * @returns {Promise} Promise with result
   */
  requestAccountDeletion(data) {
    return api.post(`/v1/users/delete-account`, data);
  }
}

export default new UserService();